import { Fragment, useState } from 'react';
import { AlertTriangle, CheckCircle2, Link2, Loader2, RefreshCw } from 'lucide-react';

import {
  ActionsTd,
  Attention,
  ExpandToggle,
  ExpandedRow,
  PayTable,
  SectionHeader,
  SubTabs,
  TableState,
  Td,
  Th,
} from '@/components/admin/PaymentsLayout';

import { Button } from '@/components/ui/button';
import { InfoDot } from '@/components/ui/InfoDot';
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import {
  useStripePayouts,
  useReconcilePayout,
  useSweepPayouts,
  useStripeWebhookStatus,
  useEnsureStripeWebhook,
  usePayoutDetail,
  type StripeDepositRow,
  type StripePayoutRow,
} from '@/hooks/admin/useStripePayouts';

/**
 * StripePayoutsPanel — the manual override for Stripe payout → QBO deposit
 * reconciliation (docs/STRIPE-RECONCILIATION.md). Each Stripe payout that hit
 * the bank should become one QBO Deposit that groups its customer Payments and
 * books the Stripe processing fee, so the deposit nets to the bank. The
 * payout.paid webhook does this automatically once STRIPE_DEPOSIT_ENABLED is
 * on; this panel lets an admin post/re-post any payout on demand.
 */

/** One-click Stripe webhook create/update — no dashboard trip. */
function WebhookSetup() {
  const status = useStripeWebhookStatus(true);
  const ensure = useEnsureStripeWebhook();
  const [secret, setSecret] = useState<string | null>(null);

  if (status.data && !status.data.stripe_configured) return null;

  const ep = status.data?.endpoint ?? null;
  const complete = ep && ep.missing_events.length === 0;
  const state = !ep ? 'not set up' : complete ? 'active — all events' : `missing ${ep.missing_events.length} event(s)`;

  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Link2 size={15} className="text-brix-primary" />
          <span className="font-semibold">Stripe webhook</span>
          <span className={cn('inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border',
            complete ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200')}>
            {state}
          </span>
          <InfoDot text="Creates or updates the Stripe webhook endpoint for this site — payouts, deposits, disputes, refunds, and bounced/failed payments — using our Stripe key. No Stripe dashboard trip needed." />
        </div>
        <Button
          variant="secondary"
          disabled={ensure.isPending}
          onClick={() => ensure.mutate(undefined, { onSuccess: (r) => setSecret(r.signing_secret ?? null) })}
        >
          {ensure.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {ep ? 'Update webhook' : 'Set up webhook'}
        </Button>
      </div>
      {ensure.isError && <p className="mt-2 text-sm text-red-700">{ensure.error.message}</p>}
      {ensure.data && (
        <p className="mt-2 text-sm text-emerald-700">
          Webhook {ensure.data.status.replace('_', ' ')} ({ensure.data.enabled_events.length} events).
        </p>
      )}
      {secret && (
        <p className="mt-2 text-xs text-slate-700 bg-white border border-brix-border rounded-lg px-3 py-2 break-all">
          <span className="font-semibold">Signing secret (optional — shown once):</span> {secret}
          <span className="block text-brix-muted mt-1">Set this as STRIPE_WEBHOOK_SECRET on Netlify to enable signature verification. Not required — events are otherwise verified by re-fetch.</span>
        </p>
      )}
    </div>
  );
}

function DepositBadge({ deposit }: { deposit: StripeDepositRow | null }) {
  if (!deposit) return <StatusBadge tone="neutral" size="sm">Not deposited</StatusBadge>;
  const map = {
    posted: { tone: 'success', label: 'Deposited' },
    reconciling: { tone: 'info', label: 'Working…' },
    needs_review: { tone: 'warning', label: 'Needs review' },
    failed: { tone: 'danger', label: 'Failed' },
    no_charges: { tone: 'neutral', label: 'No charges' },
  } as const satisfies Record<StripeDepositRow['status'], { tone: BadgeTone; label: string }>;
  const { tone, label } = map[deposit.status];
  return <StatusBadge tone={tone} size="sm">{label}</StatusBadge>;
}

/**
 * A payout is DONE once its QBO deposit posted (or it carried no charges).
 * Everything else — not reconciled yet, needs review, failed, still in
 * transit — stays on Open. Derived, never stored: a payout leaves Open the
 * moment its deposit posts, and comes back if a re-post ever fails.
 */
function isDone(p: StripePayoutRow): boolean {
  return p.deposit?.status === 'posted' || p.deposit?.status === 'no_charges';
}

function PayoutDetailRow({ payoutId }: { payoutId: string }) {
  const q = usePayoutDetail(payoutId);
  if (q.isLoading) return <p className="text-xs text-brix-muted px-1 py-2"><Loader2 size={12} className="animate-spin inline mr-1" />Loading what's in this payout…</p>;
  if (q.isError) return <p className="text-xs text-red-700 px-1 py-2">{q.error instanceof Error ? q.error.message : 'Could not load this payout'}</p>;
  const d = q.data!;
  if (d.lines.length === 0) return <p className="text-xs text-brix-muted px-1 py-2">Stripe lists nothing inside this payout.</p>;
  return (
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase tracking-tight text-brix-muted">
        <tr>
          <th className="text-left font-semibold py-1">Customer / item</th>
          <th className="text-left font-semibold py-1">QBO payment</th>
          <th className="text-right font-semibold py-1">Paid</th>
          <th className="text-right font-semibold py-1">Stripe fee</th>
          <th className="text-right font-semibold py-1">To bank</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-brix-border/60">
        {d.lines.map((l, i) => (
          <tr key={i}>
            <td className="py-1">
              {l.kind === 'charge' ? (l.customer ?? <span className="text-amber-800">No portal payment found</span>) : (
                <span className={l.kind === 'other' ? 'text-amber-800' : 'text-brix-muted'}>{l.description}</span>
              )}
              <span className="block text-[10px] text-brix-muted">{l.created.slice(0, 10)}</span>
            </td>
            <td className="py-1 font-mono">
              {l.kind !== 'charge' ? '—' : l.qbo_payment_id ? `#${l.qbo_payment_id}` : <span className="text-amber-800">not booked</span>}
            </td>
            <td className="py-1 text-right font-mono">{l.kind === 'charge' ? formatUsd(l.gross) : '—'}</td>
            <td className="py-1 text-right font-mono">{l.kind === 'charge' ? `−${formatUsd(l.fee)}` : '—'}</td>
            <td className="py-1 text-right font-mono">{formatUsd(l.net)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="border-t border-brix-border font-semibold">
        <tr>
          <td className="py-1" colSpan={2}>Total</td>
          <td className="py-1 text-right font-mono">{formatUsd(d.totals.gross)}</td>
          <td className="py-1 text-right font-mono">−{formatUsd(d.totals.deducted)}</td>
          <td className="py-1 text-right font-mono">{formatUsd(d.totals.net)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

/** A payout someone must act on: held for review, failed, or paid and never posted. */
export function payoutNeedsLook(p: StripePayoutRow): boolean {
  if (isDone(p)) return false;
  if (p.deposit?.status === 'needs_review' || p.deposit?.status === 'failed') return true;
  return p.status === 'paid' && !p.deposit;
}

export function StripePayoutsPanel() {
  const q = useStripePayouts(true);
  const reconcile = useReconcilePayout();
  const sweep = useSweepPayouts();
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const [openId, setOpenId] = useState<string | null>(null);

  if (q.data && !q.data.stripe_configured) {
    return <p className="text-sm text-brix-muted">Stripe is not configured on this site.</p>;
  }

  const all = q.data?.payouts ?? [];
  const openRows = all.filter((p) => !isDone(p));
  const doneRows = all.filter(isDone);
  const shown = tab === 'open' ? openRows : doneRows;
  const needsLook = all.filter(payoutNeedsLook).length;

  const busy = reconcile.isPending || sweep.isPending;

  return (
    <div className="space-y-3">
      <SectionHeader
        description={
          <span className="flex items-center gap-1.5">
            Each Stripe payout becomes one QBO Deposit: its customer payments grouped out of Undeposited Funds, minus the Stripe fee — so the deposit matches the bank.
            {q.data?.auto_deposit_enabled === false && <StatusBadge tone="warning" size="sm">auto-deposit off</StatusBadge>}
            <InfoDot text="On payout.paid the deposit posts automatically once STRIPE_DEPOSIT_ENABLED is on. Until then (and any time you want) use Reconcile to post one payout, or Sweep to catch every un-posted payout in the window. A payout marked 'Needs review' had a charge with no booked QBO Payment, or contained a refund or a dispute that is not booked yet — resolve then Reconcile again. Stripe's own monthly account fees (Financial Connections, Radar) are not a hold: they post as their own line on Merchant Processing Fees so the deposit still equals the bank credit." />
          </span>
        }
        actions={
          <Button variant="secondary" onClick={() => sweep.mutate(14)} disabled={busy}>
            {sweep.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Sweep 14 days
          </Button>
        }
      />

      <SubTabs
        tabs={[
          { key: 'open', label: 'Open', count: openRows.length },
          { key: 'done', label: 'Deposited', count: doneRows.length },
        ]}
        value={tab}
        onChange={(k) => { setTab(k); setOpenId(null); }}
        right={
          <Button variant="secondary" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            {q.isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        }
      />

      {tab === 'open' && (
        <Attention count={needsLook}>
          held for review or not posted yet. Open the row to see what is in the payout; the reason is under its deposit status.
        </Attention>
      )}

      {(reconcile.isError || sweep.isError) && (
        <p className="text-sm text-red-700">{(reconcile.error ?? sweep.error) instanceof Error ? ((reconcile.error ?? sweep.error) as Error).message : 'Action failed'}</p>
      )}

      <TableState
        loading={q.isLoading}
        error={q.isError ? q.error : null}
        empty={shown.length === 0}
        emptyText={
          all.length === 0
            ? 'No Stripe payouts yet.'
            : tab === 'open'
              ? 'Nothing open — every recent payout is deposited in QuickBooks.'
              : 'No payouts have been deposited in QuickBooks yet.'
        }
      >
        <PayTable minWidth={820}>
          <thead>
            <tr>
              <Th className="w-8" />
              <Th>Arrival</Th>
              <Th right>Payout</Th>
              <Th>Payout status</Th>
              <Th>QBO deposit</Th>
              <Th right>Gross / Fees</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {shown.map((p) => {
              const d = p.deposit;
              const posted = d?.status === 'posted';
              const expanded = openId === p.id;
              return (
                <Fragment key={p.id}>
                  <tr className={expanded ? 'bg-brix-surface-alt/40' : undefined}>
                    <Td className="pl-2 pr-0">
                      <ExpandToggle open={expanded} label="payout details" onClick={() => setOpenId(expanded ? null : p.id)} />
                    </Td>
                    <Td className="whitespace-nowrap text-xs">{p.arrival_date}</Td>
                    <Td right mono>{formatUsd(p.amount)}</Td>
                    <Td className="text-xs capitalize">{p.status.replace(/_/g, ' ')}</Td>
                    <Td>
                      <DepositBadge deposit={d} />
                      {d?.error && (
                        <span className="mt-0.5 flex items-start gap-1 text-[11px] text-amber-800">
                          <AlertTriangle size={11} className="mt-px shrink-0" />{d.error}
                        </span>
                      )}
                      {posted && d?.qbo_deposit_id && (
                        <span className="block text-[11px] text-brix-muted">QBO deposit #{d.qbo_deposit_id}</span>
                      )}
                    </Td>
                    <Td right mono className="text-xs">
                      {d?.gross != null ? formatUsd(Number(d.gross)) : '—'}
                      {d?.fees != null && <span className="block text-brix-muted">−{formatUsd(Number(d.fees))} fee</span>}
                    </Td>
                    <ActionsTd>
                      {posted ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> done</span>
                      ) : p.status === 'paid' ? (
                        <Button size="sm" variant="secondary" disabled={busy} onClick={() => reconcile.mutate(p.id)}>
                          {reconcile.isPending && reconcile.variables === p.id ? <Loader2 size={13} className="animate-spin" /> : null}
                          {d ? 'Re-post' : 'Reconcile'}
                        </Button>
                      ) : (
                        <span className="text-[11px] text-brix-muted">awaiting settlement</span>
                      )}
                    </ActionsTd>
                  </tr>
                  {expanded && (
                    <ExpandedRow colSpan={7}>
                      <PayoutDetailRow payoutId={p.id} />
                    </ExpandedRow>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </PayTable>
      </TableState>

      <WebhookSetup />
    </div>
  );
}
